import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { captureException } from '@sentry/nextjs'

// Save this position to sessionStorage
const saveScrollPos = (asPath: string) => {
  sessionStorage.setItem(
    `scrollPos:${asPath}`,
    JSON.stringify({ x: window.scrollX, y: window.scrollY }),
  )
}

// Scroll to the position saved in sessionStorage for this path, if there is one
const restoreScrollPos = (asPath: string) => {
  const scrollPosJson = sessionStorage.getItem(`scrollPos:${asPath}`)

  // Check if we return from SPP
  // useScrollPos is set in _app
  const isUsingScrollPosJson = sessionStorage.getItem('isUsingScrollPos')
  try {
    const scrollPos = scrollPosJson ? JSON.parse(scrollPosJson) : undefined
    const isUsingScrollPos = isUsingScrollPosJson
      ? JSON.parse(isUsingScrollPosJson)
      : undefined

    // If coming from SPP -> scroll and reset useScrollPos
    if (isUsingScrollPos) {
      if (scrollPos) {
        window.scrollTo(scrollPos.x, scrollPos.y)
      }

      sessionStorage.setItem('isUsingScrollPos', 'false')
    }

    sessionStorage.removeItem(`scrollPos:${asPath}`)
  } catch (e) {
    captureException(e)
    console.log(`Could not load scroll position history: ${e}`)
  }
}

const useScrollRestoration = () => {
  const router = useRouter()

  useEffect(() => {
    // Check that window is available
    if (!window || !(window instanceof Window)) return

    // If already set
    if (!('scrollRestoration' in window.history)) return

    let shouldScrollRestore = false

    // Set window.history object to manual scrollRestoration
    window.history.scrollRestoration = 'manual'

    // Scroll to previous position if there is one
    restoreScrollPos(router.asPath)

    // Save position before changing page
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      saveScrollPos(router.asPath)
      // eslint-disable-next-line no-param-reassign
      delete event.returnValue
    }

    const onRouteChangeStart = () => {
      saveScrollPos(router.asPath)
    }

    const onRouteChangeComplete = (url: string) => {
      if (shouldScrollRestore) {
        shouldScrollRestore = false
        /**
         * Calling with relative url, not expected asPath, so this
         * will break if there is a basePath or locale path prefix.
         */
        restoreScrollPos(url)
      }
    }

    // Add listeners
    window.addEventListener('beforeunload', onBeforeUnload)
    router.events.on('routeChangeStart', onRouteChangeStart)
    router.events.on('routeChangeComplete', onRouteChangeComplete)
    router.beforePopState(() => {
      shouldScrollRestore = true
      return true
    })

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      router.events.off('routeChangeStart', onRouteChangeStart)
      router.events.off('routeChangeComplete', onRouteChangeComplete)
      router.beforePopState(() => true)
    }
  }, [router])
}

export default useScrollRestoration
